import { useState } from 'preact/hooks';
import type { Connection } from '../../../types';
import { connectionManagementService } from '../../../services/connections/connection-management.service';
import { useScopeContext } from '../../../contexts/ScopeContext';
import { toast } from '../../../services/toast.service';

interface TestTabProps {
  connection: Connection;
  onConnectionUpdate: () => void;
}

export function TestTab({ connection, onConnectionUpdate }: TestTabProps) {
  const scope = useScopeContext();
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [testSuccessful, setTestSuccessful] = useState(false);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await connectionManagementService.testConnection(scope.teamId, connection.connectionId || connection.id);
      setTestResult(result);
      if (result.success) {
        setTestSuccessful(true);
        // Don't reload connection data here - it doesn't change from a successful test
        // Only reload after activation
      }
    } catch (err) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleActivate = async () => {
    try {
      // Activate by updating the connection's active field
      await connectionManagementService.updateConnection(scope.teamId, connection.connectionId || connection.id, {
        active: true
      });
      await onConnectionUpdate();
    } catch (err) {
      toast.error(`Failed to activate: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="alert">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span>Test your connection to verify credentials are working correctly</span>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleTestConnection}
        disabled={testing || !connection?.hasCredentials}
      >
        {testing ? (
          <>
            <span className="loading loading-spinner"></span>
            Testing...
          </>
        ) : (
          <>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
            </svg>
            Test Connection
          </>
        )}
      </button>

      {testResult && (
        <div className={`alert ${testResult.success ? 'alert-success' : 'alert-error'}`}>
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            {testResult.success ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            )}
          </svg>
          <span>{testResult.message}</span>
        </div>
      )}

      {(testSuccessful || (testResult?.success && !connection?.active)) && (
        <div className="card bg-base-200">
          <div className="card-body">
            <p className="text-success">✓ Connection test successful</p>
            <p className="text-sm opacity-70">You can now activate this connection and configure permissions</p>
            <div className="card-actions justify-end">
              <button
                className="btn btn-success"
                onClick={handleActivate}
                disabled={connection?.active}
              >
                {connection?.active ? 'Already Activated' : 'Activate Connection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
